import Pusher from 'pusher-js';
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { LIVE_PAGE, REQUEST_VIDEO_PAGE, STREAM_PAGE, TRACKING_PAGE } from '../../constants/app';
import { environment } from '../../environments/environment';
import { useToast } from '../../hooks/useToast';
import { accountState } from '../../storage/account';
import { pageState, workspaceState } from '../../storage/app';
import { updatedCurrentStateState } from '../../storage/client';
import { updatedDeliveryFencesKeyState, updatedFencesKeyState } from '../../storage/map';
import {
  internalNotificationTypeState,
  notificationsState,
  selectedNotificationTypeState,
  unreadNotificationsCountState,
} from '../../storage/notifications';
import { CurrentState } from '../../types/currentState';
import { PusherNotification } from '../../types/notification';

function PusherProvider() {
  const account = useRecoilValue(accountState);
  const workspace = useRecoilValue(workspaceState);
  const setUpdatedFencesKey = useSetRecoilState(updatedFencesKeyState);
  const setUpdatedDeliveryFencesKey = useSetRecoilState(updatedDeliveryFencesKeyState);
  const setUpdatedCurrentState = useSetRecoilState(updatedCurrentStateState);
  const setNotifications = useSetRecoilState(notificationsState);
  const internalNotificationType = useRecoilValue(internalNotificationTypeState);
  const notificationType = useRecoilValue(selectedNotificationTypeState(internalNotificationType));
  const notificationTypeRef = useRef(notificationType);
  const setUnreadNotificationsCount = useSetRecoilState(unreadNotificationsCountState);
  const workspaceRef = useRef(workspace);
  const page = useRecoilValue(pageState);
  const pusherRef = useRef<Pusher | null>(null);
  const { assetId } = useParams();
  const { showNotification } = useToast();
  const livePages = new Set([LIVE_PAGE.id, REQUEST_VIDEO_PAGE.id, STREAM_PAGE.id]);

  useEffect(() => {
    if (!pusherRef.current) {
      pusherRef.current = new Pusher(environment.pusherKey ?? '', {
        cluster: environment.pusherCluster,
      });
    }

    return () => {
      pusherRef.current?.disconnect();
      pusherRef.current = null;
    };
  }, []);

  useEffect(() => {
    workspaceRef.current = workspace;
  }, [workspace]);

  useEffect(() => {
    notificationTypeRef.current = notificationType;
  }, [notificationType]);

  useEffect(() => {
    if (!account?.pusherKey) return;
    const pusher = pusherRef.current;
    if (!pusher) return;

    const channel = pusher.subscribe(account.pusherKey);

    channel.bind('new_fence_file', (data: { fileKey: string; playSound: boolean; showBubble: boolean }) => {
      if (data.fileKey) {
        setUpdatedFencesKey(data.fileKey);
      }
    });

    channel.bind('new_delivery_fence_file', (data: { fileKey: string; playSound: boolean; showBubble: boolean }) => {
      if (data.fileKey) {
        setUpdatedDeliveryFencesKey(data.fileKey);
      }
    });

    channel.bind('new_notification', (data: PusherNotification) => {
      if (workspaceRef.current && data.subClient !== workspaceRef.current._id) return;
      if (data) {
        if (
          notificationTypeRef.current.notificationId === -1 ||
          (data.notificationType === notificationTypeRef.current.notificationId &&
            data.origin === notificationTypeRef.current.origin)
        ) {
          setNotifications((prev) => {
            const notificationId = data._id ?? data.id ?? `notification-${prev ? prev.length + 1 : 0}`;
            if (prev)
              return [
                {
                  ...data,
                  _id: notificationId,
                  read: false,
                },
                ...prev,
              ];
            else
              return [
                {
                  ...data,
                  _id: notificationId,
                  read: false,
                },
              ];
          });
        }
        setUnreadNotificationsCount((prev) => (prev ? prev + 1 : 1));
        if (data.showBubble) showNotification(data.title, data.label ?? '');
        if (data.eventType === 'alarms') {
          const audio = new Audio();
          audio.src = '/audio/alarm.wav';
          audio.load();
          audio
            .play()
            .then(() => {})
            .catch(() => {});
        }
      }
    });

    return () => {
      pusher.unsubscribe(account.pusherKey);
    };
  }, [account?.pusherKey]);

  useEffect(() => {
    if (!account?.pusherKey) return;
    if (page !== TRACKING_PAGE.id && !livePages.has(page)) return;
    if (page === TRACKING_PAGE.id && !assetId) return;
    const pusher = pusherRef.current;
    if (!pusher) return;

    if (page === TRACKING_PAGE.id && assetId) {
      const liveKey = `live_${assetId}_${account.pusherKey}`;
      const channel = pusher.subscribe(liveKey);

      channel.bind('newPosition', (data: CurrentState) => {
        if (data?.ident) {
          setUpdatedCurrentState(data);
        }
      });

      return () => {
        pusher.unsubscribe(liveKey);
      };
    } else if (livePages.has(page)) {
      const mapKey = `map_${account.pusherKey}`;
      const channel = pusher.subscribe(mapKey);

      channel.bind('newPosition', (data: CurrentState) => {
        if (data?.ident) {
          setUpdatedCurrentState(data);
        }
      });

      return () => {
        pusher.unsubscribe(mapKey);
      };
    }
  }, [account?.pusherKey, page]);

  return null;
}

export default PusherProvider;
